import toastr from 'react-hot-toast';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import { CACHE_AND_STALE_TIME } from 'constants/index';
import React from 'react';
import { useQuery } from 'react-query';
import { AIModel, AIModelResponse } from 'types/AIModels';
import { DropdownOption } from 'types/common/DropdownOption';
import { numericSort } from 'utilities/sort';
import { fetchWrapper } from 'utils/fetchWrapper';
import {
  generateFilter,
  generateSortQuery,
  generateURL,
} from 'utilities/hooks/urlGeneration';

const getAIModels = (providerId: string | undefined | null) => {
  const queryArray = [generateSortQuery('sortOrder', 'Acs')];
  if (providerId) {
    queryArray.push(generateFilter('aiProviderID', providerId));
  }

  const url = generateURL(SPARK_RESOURCE_TYPES.AI_MODELS, queryArray);
  return fetchWrapper<AIModelResponse>({
    url: url,
    method: 'GET',
  });
};

export function useAIModels(providerId: string | null | undefined) {
  const queryResult = useQuery(
    ['aiModels', providerId],
    () => getAIModels(providerId),
    {
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(`Failed to get AI Models: ${error.message}`);
        }
      },
      ...CACHE_AND_STALE_TIME,
      enabled: Boolean(providerId),
    }
  );

  const { data } = queryResult;
  const { data: aiModels } = data || {};

  const sortedAIModels = aiModels?.sort((a, b) => {
    return numericSort(a.attributes.sortOrder, b.attributes.sortOrder);
  });

  const dropdownOptions: DropdownOption[] =
    React.useMemo(
      () =>
        sortedAIModels?.map((model) => ({
          label: model.attributes.name,
          value: model.id,
          description: model.attributes.description,
        })),
      [sortedAIModels]
    ) || [];

  const idMap = aiModels?.reduce<Record<string, AIModel>>((acc, curr) => {
    return { ...acc, [curr.id]: curr };
  }, {});

  return {
    dropdownOptions,
    modelIdMap: idMap,
    aiModels,
    ...queryResult,
  };
}
